/**
 * Maps Web-Studio `design_tokens` payloads to CSS custom properties on a target root
 * (sandbox iframe or host document). No rebuild — utilities re-resolve on the next paint.
 */

/** Canonical design_tokens shape from the Web-Studio v3.2 specification. */
export interface DesignTokens {
  colors?: {
    brandPrimary?: string;
    brandAccent?: string;
  };
  fonts?: {
    brandHeading?: string;
    brandBody?: string;
  };
  spacing?: {
    brandXs?: string;
    brandSm?: string;
    brandMd?: string;
    brandLg?: string;
    brandXl?: string;
  };
}

/** CSS variable on :root ← design_tokens path */
const TOKEN_BINDINGS = {
  'colors.brandPrimary': '--ws-color-brand-primary',
  'colors.brandAccent': '--ws-color-brand-accent',
  'fonts.brandHeading': '--ws-font-brand-heading',
  'fonts.brandBody': '--ws-font-brand-body',
  'spacing.brandXs': '--ws-spacing-brand-xs',
  'spacing.brandSm': '--ws-spacing-brand-sm',
  'spacing.brandMd': '--ws-spacing-brand-md',
  'spacing.brandLg': '--ws-spacing-brand-lg',
  'spacing.brandXl': '--ws-spacing-brand-xl',
} as const satisfies Record<string, string>;

type TokenPath = keyof typeof TOKEN_BINDINGS;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function readTokenValue(tokens: DesignTokens, path: TokenPath): string | undefined {
  switch (path) {
    case 'colors.brandPrimary':
      return tokens.colors?.brandPrimary;
    case 'colors.brandAccent':
      return tokens.colors?.brandAccent;
    case 'fonts.brandHeading':
      return tokens.fonts?.brandHeading;
    case 'fonts.brandBody':
      return tokens.fonts?.brandBody;
    case 'spacing.brandXs':
      return tokens.spacing?.brandXs;
    case 'spacing.brandSm':
      return tokens.spacing?.brandSm;
    case 'spacing.brandMd':
      return tokens.spacing?.brandMd;
    case 'spacing.brandLg':
      return tokens.spacing?.brandLg;
    case 'spacing.brandXl':
      return tokens.spacing?.brandXl;
    default:
      return undefined;
  }
}

/**
 * Inject or update design tokens on the target element (defaults to `document.documentElement`).
 * Use the iframe's `contentDocument.documentElement` when sandboxed.
 */
export function applyDesignTokens(
  designTokens: DesignTokens,
  root: HTMLElement = document.documentElement,
): void {
  const style = root.style;

  for (const path of Object.keys(TOKEN_BINDINGS) as TokenPath[]) {
    const cssVar = TOKEN_BINDINGS[path];
    const value = readTokenValue(designTokens, path);

    if (isNonEmptyString(value)) {
      style.setProperty(cssVar, value.trim());
    }
  }
}

/**
 * Parse an untrusted `design_tokens` payload (e.g. postMessage from studio-shell).
 * Returns only recognized keys with non-empty string values.
 */
export function parseDesignTokens(payload: unknown): DesignTokens {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const input = payload as Record<string, unknown>;
  const colors = input.colors;
  const fonts = input.fonts;
  const spacing = input.spacing;

  const result: DesignTokens = {};

  if (colors && typeof colors === 'object') {
    const c = colors as Record<string, unknown>;
    result.colors = {};
    if (isNonEmptyString(c.brandPrimary)) result.colors.brandPrimary = c.brandPrimary.trim();
    if (isNonEmptyString(c.brandAccent)) result.colors.brandAccent = c.brandAccent.trim();
  }

  if (fonts && typeof fonts === 'object') {
    const f = fonts as Record<string, unknown>;
    result.fonts = {};
    if (isNonEmptyString(f.brandHeading)) result.fonts.brandHeading = f.brandHeading.trim();
    if (isNonEmptyString(f.brandBody)) result.fonts.brandBody = f.brandBody.trim();
  }

  if (spacing && typeof spacing === 'object') {
    const s = spacing as Record<string, unknown>;
    result.spacing = {};
    if (isNonEmptyString(s.brandXs)) result.spacing.brandXs = s.brandXs.trim();
    if (isNonEmptyString(s.brandSm)) result.spacing.brandSm = s.brandSm.trim();
    if (isNonEmptyString(s.brandMd)) result.spacing.brandMd = s.brandMd.trim();
    if (isNonEmptyString(s.brandLg)) result.spacing.brandLg = s.brandLg.trim();
    if (isNonEmptyString(s.brandXl)) result.spacing.brandXl = s.brandXl.trim();
  }

  return result;
}

/** Apply parsed tokens before React hydration to avoid a flash of default theme. */
export function bootstrapThemeFromPayload(
  payload: unknown,
  root: HTMLElement = document.documentElement,
): DesignTokens {
  const tokens = parseDesignTokens(payload);
  applyDesignTokens(tokens, root);
  return tokens;
}
